import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, ListPlus } from 'lucide-react';
import { CF_TYPE_LABELS } from '../customFields';
import type { CustomFieldsImportReport } from '../types';

/** Résultat de l'import des champs personnalisés (attributs créés + bindings). */
export function CustomFieldsReport({ report }: { report: CustomFieldsImportReport }) {
  const files = Object.entries(report.files);
  if (files.length === 0 && report.warnings.length === 0) return null;

  return (
    <Card>
      <CardHeader icon={<ListPlus />} iconColor="purple">
        <CardTitle className="text-base">Champs personnalisés</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {report.warnings.map((w) => (
          <Alert key={w} variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{w}</AlertDescription>
          </Alert>
        ))}

        {files.map(([logicalName, file]) => (
          <div key={logicalName} className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              {file.label}
              <span className="font-normal text-muted-foreground">(fichier {logicalName})</span>
              {file.usersNotFound > 0 && (
                <Badge variant="destructive">
                  {file.usersNotFound} utilisateur(s) non retrouvé(s)
                </Badge>
              )}
            </div>
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left">
                  <tr>
                    <th className="px-3 py-2">Champ</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Attribut V2</th>
                    <th className="px-3 py-2">Envoyées (async)</th>
                    <th className="px-3 py-2">Valeurs créées</th>
                    <th className="px-3 py-2">Déjà présentes</th>
                    <th className="px-3 py-2">Ignorées</th>
                  </tr>
                </thead>
                <tbody>
                  {file.columns.map((col) => (
                    <tr key={col.column} className="border-t align-top">
                      <td className="px-3 py-2">
                        <div className="font-medium">{col.label}</div>
                        <div className="text-xs text-muted-foreground font-mono">{col.column}</div>
                      </td>
                      <td className="px-3 py-2">{CF_TYPE_LABELS[col.typeCode] ?? col.typeCode}</td>
                      <td className="px-3 py-2">
                        {col.attribute === 'created' ? (
                          <Badge variant="success">Créé</Badge>
                        ) : col.attribute === 'reused' ? (
                          <Badge variant="secondary">Existant</Badge>
                        ) : (
                          <Badge variant="destructive">Erreur</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2">{col.queued ?? 0}</td>
                      <td className="px-3 py-2">{col.created}</td>
                      <td className="px-3 py-2">{col.reused}</td>
                      <td className="px-3 py-2">{col.skipped}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {file.columns.some((c) => c.errors.length > 0) && (
              <ul className="list-inside list-disc text-xs text-destructive">
                {file.columns.flatMap((c) =>
                  c.errors.map((e, i) => <li key={`${c.column}-${i}`}>{c.label} : {e}</li>),
                )}
              </ul>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
